/**
 * ModelLibrary Store
 *
 * Manages UI state for the model library, including which version (draft vs production)
 * the user is viewing for each model.
 */

import { type SetStoreState, createStore, createStoreInstance } from '@/stores/createStore';

export interface ModelLibraryState {
  /** Per-model toggle: true = viewing draft, false/absent = viewing production. */
  draftToggles: Record<string, boolean>;
}

const initialState: ModelLibraryState = {
  draftToggles: {},
};

const modelLibraryActionsFactory = (set: SetStoreState<ModelLibraryState>) => ({
  /** Mark a model as viewing draft or production in the library. */
  setDraftToggle: (modelId: string, isViewingDraft: boolean) =>
    set((draft: ModelLibraryState) => {
      draft.draftToggles[modelId] = isViewingDraft;
    }),

  /** Clear the draft toggle for a model (e.g., after lock+publish removes the draft). */
  clearDraftToggle: (modelId: string) =>
    set((draft: ModelLibraryState) => {
      delete draft.draftToggles[modelId];
    }),

  reset: () => set(() => initialState),
});

const [ModelLibraryStoreProvider, useStore, useSelector] = createStore(
  'ModelLibrary',
  initialState,
  modelLibraryActionsFactory,
);

export { ModelLibraryStoreProvider };

/** Per-model draft/production toggle state. */
export const useDraftToggles = () => useSelector((state) => state.draftToggles);

/** Actions for managing draft toggle state. */
export const useModelLibraryActions = () => useStore().actions;

/** Create an isolated store instance for tests that need to assert exact store state. */
export const createTestModelLibraryStore = () => createStoreInstance(initialState, modelLibraryActionsFactory);
